import { useCallback, useEffect, useState } from "react";

import { type MultiFactorEnrollmentStart } from "~/gql/graphqlBase.generated";
import { sanitizeAuthenticatorCode } from "~settings/security/helpers/multiFactorAuth";
import {
	type MultiFactorCompleteResult,
	type MultiFactorStartResult
} from "~settings/security/hooks/useMultiFactorMutations";

export type MultiFactorEnrollStep = "enterCode" | "recoveryCodes" | "starting";

export interface UseMultiFactorEnrollFlowParams {
	active: boolean;
	completeEnrollment: (secretBase32Encoded: string, code: string) => Promise<MultiFactorCompleteResult>;
	fallbackErrorMessage: string;
	startEnrollment: () => Promise<MultiFactorStartResult>;
}

export interface UseMultiFactorEnrollFlowReturn {
	code: string;
	codeError: string | null;
	enrollment: MultiFactorEnrollmentStart | null;
	isCompleting: boolean;
	isStarting: boolean;
	recoveryCodes: string[];
	retryStart: () => void;
	setCode: (value: string) => void;
	startError: string | null;
	step: MultiFactorEnrollStep;
	submitCode: () => Promise<void>;
}

const useMultiFactorEnrollFlow = ({
	active,
	completeEnrollment,
	fallbackErrorMessage,
	startEnrollment
}: UseMultiFactorEnrollFlowParams): UseMultiFactorEnrollFlowReturn => {
	const [step, setStep] = useState<MultiFactorEnrollStep>("starting");
	const [enrollment, setEnrollment] = useState<MultiFactorEnrollmentStart | null>(null);
	const [startError, setStartError] = useState<string | null>(null);
	const [isStarting, setIsStarting] = useState(false);
	const [code, setCodeValue] = useState("");
	const [codeError, setCodeError] = useState<string | null>(null);
	const [isCompleting, setIsCompleting] = useState(false);
	const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

	const begin = useCallback(async () => {
		setStep("starting");
		setStartError(null);
		setIsStarting(true);
		const result = await startEnrollment();
		setIsStarting(false);

		if (!result.enrollment) {
			setStartError(result.error ?? fallbackErrorMessage);
			return;
		}

		setEnrollment(result.enrollment);
		setCodeValue("");
		setCodeError(null);
		setStep("enterCode");
	}, [fallbackErrorMessage, startEnrollment]);

	useEffect(() => {
		if (active) {
			void begin();
			return;
		}

		setStep("starting");
		setEnrollment(null);
		setStartError(null);
		setCodeValue("");
		setCodeError(null);
		setRecoveryCodes([]);
	}, [active]);

	const setCode = useCallback(
		(value: string) => {
			setCodeValue(sanitizeAuthenticatorCode(value, enrollment?.digits));
			setCodeError(null);
		},
		[enrollment?.digits]
	);

	const submitCode = useCallback(async () => {
		if (!enrollment) return;

		setCodeError(null);
		setIsCompleting(true);
		const result = await completeEnrollment(enrollment.secretBase32Encoded, code);
		setIsCompleting(false);

		if (!result.ok) {
			setCodeError(result.error ?? fallbackErrorMessage);
			return;
		}

		setRecoveryCodes(result.recoveryCodes);
		setStep("recoveryCodes");
	}, [code, completeEnrollment, enrollment, fallbackErrorMessage]);

	const retryStart = useCallback(() => {
		void begin();
	}, [begin]);

	return {
		code,
		codeError,
		enrollment,
		isCompleting,
		isStarting,
		recoveryCodes,
		retryStart,
		setCode,
		startError,
		step,
		submitCode
	};
};

export default useMultiFactorEnrollFlow;
